import { ChatRoom, MinimizedChat } from '@shared/types';
import { pipe } from '@shared/util/function';
import { useSelector } from 'react-redux';
import { Reducer } from 'redux';
import { createSelector } from 'reselect';

const initialState = {
  chatRooms: [] as ChatRoom[],
  showOnlyUnread: false,
  minimizedChats: [] as MinimizedChat[],
};

type CurrentChatSelectorOptions = {
  showOnlyUnread?: boolean;
  showOnlyActive?: boolean;
};

export type State = typeof initialState;

const sortByDate = (ChatRooms: ChatRoom[]) =>
  ChatRooms.slice().sort(
    (chatRoomA: ChatRoom, chatRoomB: ChatRoom) =>
      new Date(chatRoomB.updatedAt).getTime() - new Date(chatRoomA.updatedAt).getTime(),
  );

const filterOnlyUnRead = (chatRooms: ChatRoom[]) =>
  chatRooms.filter((chatRoom) => chatRoom.unread > 0);

const filterOnlyActive = (chatRooms: ChatRoom[]) =>
  chatRooms.filter((chatRoom) => chatRoom.status === 'active');

/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
const ADD_CHAT_ROOM = 'ADD_CHAT_ROOM' as const;
const CHAT_ROOMS_FETCHED = 'CHAT_ROOMS_FETCHED' as const;
const SWITCH_SHOW_ONLY_UNREAD = 'SWITCH_SHOW_ONLY_UNREAD' as const;
const UPDATE_CHAT_ROOM = 'UPDATE_CHAT_ROOM' as const;
const MINIMIZE_CHAT = 'MINIMIZE_CHAT' as const;
const CLOSE_MINIMIZED_CHAT = 'CLOSE_MINIMIZED_CHAT' as const;
/* eslint-enable @typescript-eslint/no-unnecessary-type-assertion */

export const actionCreators = {
  updateChatRoom: (chatRoomId: string, newData: Partial<ChatRoom>) => ({
    type: UPDATE_CHAT_ROOM,
    data: { chatRoomId, newData },
  }),
  addChatRoom: (data: ChatRoom) => ({ type: ADD_CHAT_ROOM, data }),
  chatRoomsFetched: (data: ChatRoom[]) => ({ type: CHAT_ROOMS_FETCHED, data }),
  switchShowOnlyUnread: () => ({ type: SWITCH_SHOW_ONLY_UNREAD }),
  minimizeChat: (minimizedChat: MinimizedChat) => ({ type: MINIMIZE_CHAT, data: minimizedChat }),
  closeMinimizedChat: (minimizedChatId: string) => ({
    type: CLOSE_MINIMIZED_CHAT,
    data: minimizedChatId,
  }),
};

type ChatAction = ReturnType<(typeof actionCreators)[keyof typeof actionCreators]>;

const chat: Reducer<State, ChatAction> = (state = initialState, action) => {
  switch (action.type) {
    case MINIMIZE_CHAT:
      return { ...state, minimizedChats: [action.data, ...state.minimizedChats].slice(0, 3) };
    case CLOSE_MINIMIZED_CHAT:
      return {
        ...state,
        minimizedChats: state.minimizedChats.filter(
          (minimizedChat) => minimizedChat.id !== action.data,
        ),
      };
    case ADD_CHAT_ROOM:
      return {
        ...state,
        chatRooms: [
          ...(state.chatRooms.find((room) => room.id === action.data.id)
            ? [action.data, ...state.chatRooms.filter((room) => room.id !== action.data.id)]
            : [action.data, ...state.chatRooms]),
        ],
      };
    case CHAT_ROOMS_FETCHED:
      return { ...state, chatRooms: action.data };
    case UPDATE_CHAT_ROOM:
      return {
        ...state,
        chatRooms: state.chatRooms.map((chatroom) => {
          if (chatroom.id === action.data.chatRoomId) {
            return {
              ...chatroom,
              ...action.data.newData,
            };
          }
          return chatroom;
        }),
      };
    case SWITCH_SHOW_ONLY_UNREAD:
      return { ...state, showOnlyUnread: !state.showOnlyUnread };
    default:
      return state;
  }
};

export default chat;

/// SELECTORS

type RootSelectorType = { chat: State };

const rootSelector = (state: RootSelectorType) => state.chat;

// const pipeA = pipe(sortByDate);
const roomsSelector = createSelector(
  [rootSelector, (_, options?: CurrentChatSelectorOptions) => options],
  ({ chatRooms }, options?: CurrentChatSelectorOptions) => {
    const chatRoomTransformations = [sortByDate];
    if (options?.showOnlyUnread) {
      chatRoomTransformations.push(filterOnlyUnRead);
    }
    if (options?.showOnlyActive) {
      chatRoomTransformations.push(filterOnlyActive);
    }
    return pipe(chatRooms, chatRoomTransformations);
  },
);

const currentChatRoomSelector = createSelector(
  [rootSelector, (_, chatRoomId: string) => chatRoomId],
  (items, chatRoomId) => items.chatRooms.find((item) => item.id === chatRoomId),
);

const chatSelector = createSelector(rootSelector, (state) => state);
const minimizedChatRooms = createSelector(rootSelector, (state) => state.minimizedChats);

const lastUnreadTimestamp = createSelector(rootSelector, (state) =>
  state.chatRooms.reduce(
    (acc, chatRoom) => (chatRoom.unread > 0 && acc < chatRoom.updatedAt ? chatRoom.updatedAt : acc),
    '',
  ),
);

export const useMinimizedChatRooms = () => useSelector(minimizedChatRooms);

export const useChat = () => useSelector(chatSelector);
export const useLastUnreadTimestamp = () => useSelector(lastUnreadTimestamp);

export const useCurrentChatList = (options?: CurrentChatSelectorOptions) =>
  useSelector((state: RootSelectorType) => roomsSelector(state, options));

export const useHasUnreadMessages = () =>
  useSelector((state: RootSelectorType) => roomsSelector(state).find((chat) => chat.unread > 0));

export const useCurrentChat = (chatRoomId: string) =>
  useSelector((state: RootSelectorType) => currentChatRoomSelector(state, chatRoomId));
